import React, {useState} from 'react'
import {Button, CopyText, Icon, Modal} from '@therms/atalaya'
import {
  IdLabel,
  ImageSmall,
  LocationActiveServicePeriods,
  LocationGroupTags,
  LocationName,
  makeAdminLocationURL,
  RPCDocs,
  useCurrentUserAnPCheck,
} from '@therms/react-modules'
import {formatAddress} from '@therms/web-js'
import {FaExpandAlt} from 'react-icons/fa'
import {LocationActivitySummary} from './LocationActivitySummary.tsx'
import {MdOpenInNew, MdPerson, MdPersonPin} from 'react-icons/md'
import RoleElement = RPCDocs.Auth.GetIdentityInfo.v1.Roles

interface LocationMobileHeaderProps {
  activitySummary?: RPCDocs.Locations.GetLocationActivitySummary.v1.ActivitySummary
  location: RPCDocs.Locations.GetLocationProfile.v1.Location
  locationId: string
}

export function LocationMobileHeader({
  activitySummary,
  location,
  locationId,
}: LocationMobileHeaderProps) {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)

  const hasAdminAccess = useCurrentUserAnPCheck(({anp}) =>
    anp.roles.includes(RoleElement.Admin),
  )

  const appliedServicePeriods = location.servicePeriods?.applied ?? 0

  const hasActivity =
    !!activitySummary &&
    (!!activitySummary.onSiteUsers?.length ||
      !!activitySummary.nearbyUsers?.length ||
      !!activitySummary.lastActivityLogEntry ||
      !!activitySummary.lastOnSiteUser)

  return (
    <>
      {/* Inactive location banner */}
      {!location.active && (
        <div className="bg-critical-faded text-color-critical border-critical p-xs rounded border text-center font-semibold">
          Inactive Location
        </div>
      )}

      <div
        className="hover:bg-surface-strong active:bg-surface-subtle p-sm bg-surface relative cursor-pointer rounded transition-colors"
        onClick={() => setIsDetailsModalOpen(true)}
      >
        <div className="top-sm right-sm absolute flex justify-end">
          <Icon Icon={FaExpandAlt} size="xs" color="neutral" />
        </div>

        <div className="flex items-center">
          {!!location.primaryImageUrl && (
            <div className="mr-sm shrink-0">
              <ImageSmall
                rounded
                src={location.primaryImageUrl}
                className="h-14 w-14"
                lightbox
              />
            </div>
          )}

          <div className="truncate leading-5">
            <IdLabel
              className="text-color-subtle text-xs font-bold"
              idLabel={location.idLabel}
            />

            {Boolean(
              activitySummary?.onSiteUsers || activitySummary?.nearbyUsers,
            ) && <span className="px-xxs" />}

            {!!activitySummary?.onSiteUsers?.length && (
              <span className="text-color-subtle ml-xxs inline-flex items-center text-xs font-bold">
                <span>{activitySummary.onSiteUsers.length}</span>{' '}
                <Icon Icon={MdPerson} size="xs" />
              </span>
            )}

            {!!activitySummary?.nearbyUsers?.length && (
              <span className="text-color-subtle ml-xxs inline-flex items-center text-xs font-bold">
                <span>{activitySummary.nearbyUsers.length}</span>{' '}
                <Icon Icon={MdPersonPin} size="xs" />
              </span>
            )}

            <div className="truncate font-bold">
              <span className="text-strong">{location.name}</span>
              {!!location.parentLocationId && (
                <span className="text-color-subtle text-sm font-thin">
                  {' '}
                  (<LocationName locationId={location.parentLocationId} />)
                </span>
              )}
            </div>

            {!!location.address && (
              <div className="text-color-neutral truncate text-xs font-light">
                {formatAddress(location.address, false)}
              </div>
            )}
          </div>
        </div>
      </div>
      {isDetailsModalOpen && (
        <Modal
          header={
            <div className="truncate">
              <span className="text-md text-color-strong truncate font-medium">
                {location.name}
              </span>

              {!!location.idLabel && (
                <>
                  {' '}
                  <IdLabel
                    className="text-color-subtle text-xs font-bold"
                    idLabel={location.idLabel}
                  />
                </>
              )}

              {location.parentLocationId && (
                <span className="text-color-subtle text-sm font-light">
                  {' '}
                  (<LocationName locationId={location.parentLocationId} />)
                </span>
              )}
            </div>
          }
          closeHandler={() => setIsDetailsModalOpen(false)}
        >
          {!!location.primaryImageUrl && (
            <ImageSmall src={location.primaryImageUrl} fluid lightbox rounded />
          )}

          <div className="mt-xxs bg-inherit">
            {location.address && (
              <CopyText alwaysShow value={formatAddress(location.address)}>
                <span className="text-sm">
                  {formatAddress(location.address, false)}
                </span>
              </CopyText>
            )}
          </div>

          {hasAdminAccess && (
            <Button
              className="mt-xs w-full"
              outline
              size="xs"
              subtle
              variant="neutral"
            >
              <a
                href={makeAdminLocationURL(locationId)}
                target="_blank"
                rel="noreferrer noopener nofollow"
              >
                <div className="gap-x-xxs p-xxs flex items-center">
                  <span>Open in ADMIN</span>
                  <Icon Icon={MdOpenInNew} size="xs" />
                </div>
              </a>
            </Button>
          )}

          <div className="gap-xs mt-xs flex flex-wrap">
            <LocationGroupTags locationGroupIds={location.locationGroupIds} />
          </div>

          {hasActivity && (
            <div data-sticky className="py-sm sticky bg-inherit shadow">
              <div className="bg-border h-px" />
            </div>
          )}

          {location.settings.modules?.servicePeriods &&
            appliedServicePeriods > 0 && (
              <div className="mt-sm mb-base">
                <LocationActiveServicePeriods
                  applied={appliedServicePeriods}
                  locationId={locationId}
                />
              </div>
            )}

          {hasActivity && (
            <LocationActivitySummary activitySummary={activitySummary} />
          )}
        </Modal>
      )}
    </>
  )
}
